/**
 * Wallet signal emitter — the client side of the telemetry contract.
 *
 *   import { emitSignal } from "@/lib/wallet-signal";
 *   emitSignal("glossary.term.viewed.v1", { term: "TCF", dwell_ms: 4200 });
 *
 * Signals are best-effort: fire-and-forget. They never block UI. Failed
 * requests are silently dropped (network partition on a consumer device
 * should never break their journey).
 */
import axios from "axios";
import { loadWallet, updateWallet } from "@/lib/wallet-storage";
import { enqueueOfflineRequest } from "@/lib/pwa";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

function getDeviceId() {
  const w = loadWallet() || {};
  if (w.device_id) return w.device_id;
  const id = "dev_" + Math.random().toString(36).slice(2, 12) + Date.now().toString(36).slice(-4);
  updateWallet({ device_id: id });
  return id;
}

function getSessionId() {
  let sid = sessionStorage.getItem("altara.wallet.session");
  if (!sid) {
    sid = "ses_" + Math.random().toString(36).slice(2, 10) + Date.now().toString(36).slice(-4);
    sessionStorage.setItem("altara.wallet.session", sid);
  }
  return sid;
}

export function emitSignal(eventType, payload = {}, extra = {}) {
  try {
    const wallet = loadWallet() || {};
    const body = {
      event_type: eventType,
      device_id: getDeviceId(),
      consumer_id: wallet.consumer_id || null,
      tenant_id: extra.tenant_id || null,
      journey_id: extra.journey_id || null,
      session_id: getSessionId(),
      payload,
    };
    // Offline → queue for retry. Online → fire-and-forget.
    if (typeof navigator !== "undefined" && navigator.onLine === false) {
      enqueueOfflineRequest({ url: `${API}/wallet/signal`, method: "POST", body });
      return;
    }
    axios.post(`${API}/wallet/signal`, body).catch(() => { /* best-effort */ });
  } catch { /* never block UI */ }
}

/**
 * useWalletSignal — thin hook so components can fire without prop-drilling.
 * Returns a stable function that captures optional page context.
 */
export function useWalletSignal(defaultContext = {}) {
  return (eventType, payload = {}, overrides = {}) =>
    emitSignal(eventType, { ...defaultContext, ...payload }, overrides);
}

/** One-time "wallet opened" ping on app boot. */
export function pingWalletOpen(context = {}) {
  emitSignal("wallet.opened.v1", {
    language: (loadWallet() || {}).language || "en",
    accessibility: (loadWallet() || {}).accessibility || {},
    screen_w: window.innerWidth, screen_h: window.innerHeight,
    referrer_kind: context.referrer_kind || "direct",
  });
}
